import { useQuery } from '@tanstack/react-query';
import { api } from '../client';

// ── Tipos ────────────────────────────────────────────────────────────

export interface FrequenciaAluno {
  alunoId: string;
  nome: string;
  matricula?: string;
  totalDias: number;
  presentes: number;
  ausentes: number;
  percentual: number;
  emRisco: boolean;
  porBimestre: {
    bimestre: number;
    totalDias: number;
    presentes: number;
    percentual: number;
  }[];
}

export interface FrequenciaTurmaResponse {
  turmaId: string;
  anoLetivo: number;
  totalAlunos: number;
  mediaFrequencia: number;
  alunosEmRisco: number;
  alunos: FrequenciaAluno[];
}

export interface ConsolidacaoResponse {
  escolaId: string;
  anoLetivo: number;
  escopo?: 'PROFESSOR' | 'ESCOLA';
  resumo: {
    totalAlunos: number;
    totalTurmas: number;
    mediaFrequencia: number;
    totalPresencasDias: number;
    totalAusencias: number;
    totalIncidentes: number;
    totalAvaliacoes: number;
    alunosEmRisco?: number | null;
  };
  turmas: {
    id: string;
    nome: string;
    serie: string;
    turno: string;
    totalAlunos: number;
  }[];
}

export interface BoletimMateria {
  materiaId: string;
  materiaNome: string;
  bimestres: Record<number, number>;
  mediaFinal: number;
}

export interface BoletimResponse {
  aluno: {
    id: string;
    nome: string;
    matricula?: string;
    turma?: string;
    serie?: string;
    turno?: string;
    escola?: string;
  };
  anoLetivo: number;
  materias: BoletimMateria[];
  frequencia: {
    totalDias: number;
    presentes: number;
    percentual: number;
  };
}

// ── Hooks ────────────────────────────────────────────────────────────

const BASE = '/relatorios';

/**
 * Frequencia detalhada por turma com bimestres.
 * GET /relatorios/frequencia/:escolaId/:turmaId
 */
export function useFrequenciaTurma(escolaId?: string, turmaId?: string, anoLetivo?: number) {
  return useQuery({
    queryKey: ['relatorios', 'frequencia', escolaId, turmaId, anoLetivo],
    queryFn: async () => {
      const { data } = await api.get<FrequenciaTurmaResponse>(
        `${BASE}/frequencia/${escolaId}/${turmaId}`,
        { params: { anoLetivo } },
      );
      return data;
    },
    enabled: !!escolaId && !!turmaId,
  });
}

/**
 * Consolidacao geral da escola.
 * GET /relatorios/consolidacao/:escolaId
 */
export function useConsolidacao(escolaId?: string, anoLetivo?: number) {
  return useQuery({
    queryKey: ['relatorios', 'consolidacao', escolaId, anoLetivo],
    queryFn: async () => {
      const { data } = await api.get<ConsolidacaoResponse>(
        `${BASE}/consolidacao/${escolaId}`,
        { params: { anoLetivo } },
      );
      return data;
    },
    enabled: !!escolaId,
  });
}

/**
 * Boletim individual do aluno.
 * GET /relatorios/boletim/:alunoId
 */
export function useBoletim(alunoId?: string, anoLetivo?: number) {
  return useQuery({
    queryKey: ['relatorios', 'boletim', alunoId, anoLetivo],
    queryFn: async () => {
      const { data } = await api.get<BoletimResponse>(
        `${BASE}/boletim/${alunoId}`,
        { params: { anoLetivo } },
      );
      return data;
    },
    enabled: !!alunoId,
  });
}
